// @ts-strict-ignore
import queryString from 'query-string';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';

import { Box } from 'components/Box';
import {
  Button,
  SecondaryButtonDangerLink,
  SecondaryButtonLink,
} from 'components/Button';
import Hide from 'components/Hide';
import { Icon } from 'components/Icon';
import { Image } from 'components/Image';
import { LanguageSelectionMenu } from 'components/LanguageSelectionMenu';
import { TrackedLink } from 'components/Link';
import { IdealistLogoLegacy } from 'components/Logo';
import Text, { H4 } from 'components/Text';
import { ClientOnly } from 'containers/ClientOnly';
import { setSearchFilter } from 'store/main/ducks/mainSearch';
import { showModal } from 'store/main/ducks/modal';
import { useMainDispatch } from 'store/main/hooks/useMainDispatch';
import { breakpoints, colors, gutterWidth, transition } from 'theme';
import { trackClicked } from 'utils/analytics';
import { LanguageLocale } from 'utils/constants';
import { hasResumeDb } from 'utils/resumeDatabase';
import { getHomeUrl, listingSearchHome } from 'utils/searchHomePages';
import { pathnameEqual } from 'utils/url';

import { PageFooter } from './PageFooter';
import { PageHeaderAccountDropdown } from './PageHeaderAccountDropdown';
import { PageHeaderAmpPostListingLink } from './PageHeaderAmpPostListingLink';
import { PAGE_HEADER_NAV, PAGE_HEADER_PATH_MAP } from './PageHeaderConstants';
import { PageHeaderContainer } from './PageHeaderContainer';
import { PageHeaderForOrganizations } from './PageHeaderForOrganizations';
import { PageHeaderMobileSectionTitle } from './PageHeaderMobileSectionTitle';
import { PageHeaderNavLink } from './PageHeaderNavLink';
import { PageHeaderNavLinks } from './PageHeaderNavLinks';
import { PageHeaderPostListingLink } from './PageHeaderPostListingLink';
import { PageHeaderSeparator } from './PageHeaderSeparator';
import { PageHeaderTopNavLink } from './PageHeaderTopNavLink';

const getNavOptions = (section: 'MAIN' | 'STAFF') =>
  section === 'STAFF' ? PAGE_HEADER_NAV.STAFF : PAGE_HEADER_NAV.MAIN;

const LoginLink = styled.a`
  color: ${colors.battleshipGrey};
  font-size: inherit;
  transition: color ${transition};
  &,
  &:hover,
  &:focus {
    text-decoration: none;
  }
  &:hover,
  &:focus {
    color: ${colors.waterBlue};
  }
`;

const StyledLogo = styled(IdealistLogoLegacy)`
  height: 36px;
  @media (min-width: ${breakpoints[2]}) {
    height: 28px;
  }
  @media (min-width: 1350px) {
    height: 36px;
  }
`;

type Props = {
  pageUrl: Record<LanguageLocale, string | undefined>;
  minimalNav?: boolean;
  navSection?: 'MAIN' | 'STAFF';
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toggleSearch: (...args: Array<any>) => any;
  withSearchHero?: boolean;
  requestIsAmp?: boolean;
};

export function PageHeader({
  pageUrl,
  minimalNav,
  navSection = 'MAIN',
  toggleSearch,
  withSearchHero,
  requestIsAmp,
}: Props) {
  const { currentUser } = useSelector((state) => ({
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'currentUser' does not exist on type 'Def... Remove this comment to see the full error message
    currentUser: state.currentUser,
  }));
  const dispatch = useMainDispatch();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const redirectTo = location ? `${location.pathname}${location.search}` : '';
  const redirectParam = queryString.stringify({
    to: redirectTo,
  });
  const params = location ? queryString.parse(location.search) : {};
  const isStaff = navSection === 'STAFF';
  // TODO: Fix this the next time the file is edited.

  const fullSize = (withSearchHero || requestIsAmp) && !minimalNav;
  const isActive = useCallback(
    (type) => {
      // $FlowFixMe: Flow can't handle the staff-specific types
      const h = listingSearchHome(type);
      // staff
      return isStaff
        ? location &&
            type !== 'HOME' &&
            (location.pathname.includes(PAGE_HEADER_PATH_MAP[type]) ||
              (type === 'QUEUES' && location.pathname.includes('staff/queues'))) // non-staff: grad program special-cased
        : (h && pathnameEqual(h, location.pathname)) || params.type === type;
    },
    [isStaff, location, params.type]
  );
  const navInactive = (
    isStaff ? PAGE_HEADER_NAV.STAFF : PAGE_HEADER_NAV.MAIN
  ).every((t) => !isActive(t));
  const nonAmpUrl = location.pathname.replace('/amp/', '/').replace(/#.+$/, '');
  const clickLogin = useCallback(
    (e) => {
      e.preventDefault();
      trackClicked('Log In Header');
      dispatch(
        showModal('LOGIN', {
          redirectTo,
          manualLogin: true,
        })
      );
    },
    [redirectTo, dispatch]
  );

  const closeSearch = () => setMenuOpen(false);

  useEffect(() => {
    if (location.hash === '#search') {
      toggleSearch(true);
    } else if (location.hash === '#menu') {
      setMenuOpen(true);
    }
  }, [location.hash, toggleSearch]);
  const mainLinks = useMemo(
    () => (
      <>
        {getNavOptions(navSection).map((type, index) => {
          const isSpanishBlog = type === 'BLOG' && CURRENT_LOCALE === 'es';
          const linkToPath =
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line no-nested-ternary
            type === 'HOME'
              ? '/' // TODO: Fix this the next time the file is edited.
              : // eslint-disable-next-line no-nested-ternary
              isStaff
              ? `/${
                  PAGE_HEADER_PATH_MAP[type]
                    ? PAGE_HEADER_PATH_MAP[type]
                    : '/staff'
                }`
              : isSpanishBlog
              ? '/es/accion'
              : // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                getHomeUrl(type as any as ListingType);

          // Grad Schools are the only header link that have a content page
          // Because we want to avoid a redirect after render when we visit a landing page,
          // pass state to avoid landing on the search page
          const linkTo =
            type !== 'GRAD_PROGRAM'
              ? linkToPath
              : {
                  pathname: linkToPath,
                  state: { redirectToContent: true },
                };

          return (
            <Box height="100%" key={type}>
              <PageHeaderTopNavLink
                navSection={navSection}
                index={index}
                linkTo={linkTo}
                isStaff={isStaff}
                type={type}
                // @ts-expect-error -- incorrect type for dispatch
                setSearchFilter={dispatch(setSearchFilter)}
                isActive={isActive}
                navInactive={navInactive}
                fullSize={fullSize}
                external={false}
              />
            </Box>
          );
        })}
      </>
    ),
    [navSection, isStaff, dispatch, isActive, navInactive, fullSize]
  );

  const mobileLogin = useMemo(
    () => (
      <ClientOnly>
        {currentUser.id ? (
          <SecondaryButtonDangerLink
            href="/logout"
            size="large"
            width="100%"
            px="0 !important"
            py="17px !important"
          >
            {getText('Log Out')}
          </SecondaryButtonDangerLink>
        ) : (
          <SecondaryButtonLink
            size="large"
            width="100%"
            px="0 !important"
            py="17px !important"
            blue
            href={`/login?${redirectParam}`}
            onClick={clickLogin}
            data-qa-id="login-button-link"
            aria-label={getText('Log In')}
          >
            <Hide lg>{getText('Log In')}</Hide>
            <Hide xs sm md xl>
              <Icon
                initialColorName="todo-replace-me"
                name="person"
                size={16}
                opacity={0.8}
              />
            </Hide>
          </SecondaryButtonLink>
        )}
      </ClientOnly>
    ),
    [clickLogin, currentUser.id, redirectParam]
  );

  return (
    <PageHeaderContainer
      $fullSize={fullSize}
      position="relative"
      zIndex="101"
      height={[60, null, null, fullSize ? 72 : 40]}
    >
      <Box
        classes={['flex', 'align-center', 'justify-between']}
        position={['fixed', null, null, 'relative']}
        top={0}
        left={0}
        width={1}
        height={[60, null, null, fullSize ? 72 : 40]}
        px={gutterWidth / 2}
        bg="white"
        boxShadow={['0 2px 4px rgba(0,0,0,0.075)', null, null, 'none']}
        pointerEvents="auto"
      >
        {requestIsAmp ? (
          <Box mr={24}>
            <Box
              component={TrackedLink}
              href="/"
              title="Idealist"
              eventName="Idealist Logo Header"
              data-qa-id="header-logo-link"
              classes={['flex']}
            >
              <Image
                src="/static/images/logo.png"
                srcSet="/static/images/logo.png, /static/images/logo@2x.png 2x, /static/images/logo@3x.png 3x"
                width={114}
                height={36}
                alt="Idealist.org"
              />
            </Box>
          </Box>
        ) : (
          <Box
            mr={24}
            flex="0 0 auto"
            display={[null, null, null, !fullSize && 'none']}
          >
            <TrackedLink
              href={isStaff ? '/staff' : '/'}
              title={`Idealist${isStaff ? ' Staff' : ''}`}
              eventName="Idealist Logo Header"
              data-qa-id="header-logo-link"
            >
              <StyledLogo showStaffMarker={isStaff} />
            </TrackedLink>
          </Box>
        )}

        <Hide lg xl>
          <Box ml="auto" classes={['flex', 'align-center']}>
            {requestIsAmp ? (
              <>
                <a
                  href={`${nonAmpUrl}#search`}
                  aria-label={getText('Toggle search')}
                  title={getText('Toggle search')}
                  data-qa-id="search-menu-button"
                >
                  <Icon
                    name="search"
                    size={22}
                    keepColorOnLoad
                    initialColorName="charcoalGrey"
                  />
                </a>

                <Box
                  as="a"
                  href={`${nonAmpUrl}#menu`}
                  aria-label={getText('Toggle menu')}
                  title={getText('Toggle menu')}
                  ml={16}
                  data-qa-id="mobile-menu-button"
                >
                  <Icon
                    name="hamburger"
                    size={22}
                    keepColorOnLoad
                    initialColorName="charcoalGrey"
                  />
                </Box>
              </>
            ) : (
              <>
                <Button
                  onClick={toggleSearch}
                  aria-label={getText('Toggle search')}
                  title={getText('Toggle search')}
                  data-qa-id="search-menu-button"
                >
                  <Icon initialColorName="black" name="search" size={22} />
                </Button>

                <Button
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-label={getText('Toggle menu')}
                  title={getText('Toggle menu')}
                  ml={16}
                  data-qa-id="mobile-menu-button"
                >
                  <Icon initialColorName="black" name="hamburger" size={22} />
                </Button>
              </>
            )}
          </Box>
        </Hide>

        <PageHeaderNavLinks
          flex="1 1 auto"
          $menuOpen={menuOpen}
          onClick={() => setMenuOpen(false)}
          height={[null, null, null, '100%']}
        >
          {/* @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element; lg: true; xl: true; hei... Remove this comment to see the full error message */}
          <Hide lg xl height={[null, null, null, '100%']}>
            <Box
              classes={['flex', 'align-center', 'justify-between']}
              px={gutterWidth / 2}
              height={60}
              width="100%"
              borderBottom={`1px solid ${colors.aluminum}`}
            >
              <H4 mb={0} color={colors.waterBlue}>
                {currentUser.id
                  ? /* TRANSLATORS: user greeting at top of page */
                    sprintf(getText('Hi, %(name)s!'), {
                      name: currentUser.firstName || currentUser.email,
                    })
                  : getText('Menu')}
              </H4>

              <Button
                onClick={closeSearch}
                aria-label={getText('Close search')}
                ml="auto"
              >
                <Icon
                  name="close"
                  size={16}
                  keepColorOnLoad
                  initialColorName="waterBlue"
                />
              </Button>
            </Box>
          </Hide>

          <Box
            display={['block', null, null, 'flex']}
            height={[null, null, null, '100%']}
            justifyContent="space-between"
            p={[gutterWidth / 2, null, null, 0]}
            pb={[0, null, null, 0]}
          >
            <Box
              display={['block', null, null, 'flex']}
              alignItems="center"
              mt="1px"
            >
              {mainLinks}
            </Box>

            <Box
              display={['block', null, null, 'flex']}
              alignItems="center"
              ml="auto"
              mt={[0, null, null, '1px']}
            >
              {currentUser.id ? (
                <>
                  <Hide lg xl>
                    <PageHeaderMobileSectionTitle>
                      {getText('Settings')}
                    </PageHeaderMobileSectionTitle>

                    {hasResumeDb(currentUser) && (
                      <PageHeaderNavLink
                        isStaff={isStaff}
                        type="USER_DASHBOARD"
                        to="/dashboard/employment-profile"
                      >
                        {getText('Employment Profile')}
                      </PageHeaderNavLink>
                    )}

                    <PageHeaderNavLink
                      isStaff={isStaff}
                      type="USER_DASHBOARD"
                      to="/dashboard/account-settings"
                    >
                      {getText('My Account')}
                    </PageHeaderNavLink>

                    {currentUser.orgs && currentUser.orgs.length > 0 && (
                      <>
                        <PageHeaderMobileSectionTitle>
                          {getText('My Organizations')}
                        </PageHeaderMobileSectionTitle>

                        {currentUser.orgs.map((org) => (
                          <Box key={org.id}>
                            <PageHeaderNavLink
                              isStaff={isStaff}
                              type="ORG_DASHBOARD"
                              to={
                                org.hasGradProgram
                                  ? `/admin/${org.id}/grad-program`
                                  : `/admin/${org.id}`
                              }
                            >
                              {org.name}
                            </PageHeaderNavLink>
                          </Box>
                        ))}
                      </>
                    )}
                  </Hide>

                  <ClientOnly>
                    <Hide xs sm md>
                      <Box mt="1px">
                        <PageHeaderAccountDropdown
                          currentUser={currentUser}
                          isStaff={isStaff}
                        />
                      </Box>
                    </Hide>
                  </ClientOnly>

                  <PageHeaderSeparator />
                </>
              ) : (
                <Box display={['none', null, null, 'flex']} alignItems="center">
                  <LoginLink
                    href={`/login?${redirectParam}`}
                    onClick={clickLogin}
                    data-qa-id="login-link"
                    aria-label={getText('Log In')}
                  >
                    {(currentUser.isLoaded || requestIsAmp) &&
                      getText('Log In')}
                  </LoginLink>
                  <PageHeaderSeparator ml={12} />
                </Box>
              )}

              <Hide xs sm md>
                {requestIsAmp ? (
                  <Box
                    component={LoginLink}
                    mx={12}
                    classes={['flex', 'align-center']}
                    href={`${nonAmpUrl}#search`}
                    aria-label={getText('Toggle search')}
                    title={getText('Toggle search')}
                    data-qa-id="search-menu-button"
                  >
                    <Box mr="6px">
                      <Icon
                        name="search"
                        size={18}
                        initialColorName="battleshipGrey"
                      />
                    </Box>
                    {getText('Search')}
                  </Box>
                ) : (
                  <Box mt="1px">
                    <LanguageSelectionMenu pageUrl={pageUrl} />
                  </Box>
                )}
              </Hide>

              {!isStaff && (
                <>
                  {/* @ts-expect-error ts-migrate(2322) FIXME: Type '{ location: Location<unknown>; }' is not ass... Remove this comment to see the full error message */}
                  <PageHeaderForOrganizations location={location} />
                  <PageHeaderSeparator mr={12} />
                  <PageHeaderMobileSectionTitle />

                  {requestIsAmp ? (
                    <PageHeaderAmpPostListingLink />
                  ) : (
                    <PageHeaderPostListingLink />
                  )}
                  <Hide lg xl>
                    <PageHeaderNavLink
                      to="/organizations/create"
                      data-qa-id="create-org"
                      onClick={() => {
                        trackClicked('Add Your Organization');
                      }}
                      type="ORG"
                    >
                      <Box classes={['flex', 'align-center']}>
                        <Box
                          mr={[14, null, null, '6px']}
                          mt="4px"
                          opacity={0.8}
                        >
                          <Icon
                            initialColorName="todo-replace-me"
                            name="circle-add"
                            size={18}
                          />
                        </Box>
                        <Text fontSize="inherit" fontWeight="normal">
                          {getText('Add Your Organization')}
                        </Text>
                      </Box>
                    </PageHeaderNavLink>

                    <PageHeaderMobileSectionTitle />

                    {mobileLogin}

                    <Box
                      width={`calc(100% + ${gutterWidth}px)`}
                      ml={-gutterWidth / 2}
                      mt={gutterWidth}
                      borderBottom={`6px solid ${colors.waterBlue}`}
                    >
                      <PageFooter url={pageUrl} inPageHeader />
                    </Box>
                  </Hide>
                </>
              )}
            </Box>
          </Box>
        </PageHeaderNavLinks>
      </Box>
    </PageHeaderContainer>
  );
}
